import React from 'react';
import { TiltCard } from '../../components/TiltCard/TiltCard';
import { RippleButton } from '../../components/RippleButton/RippleButton';
import './Pricing.scss';

const plans = [
  {
    id: 'essentials',
    name: 'Essentials',
    price: 0,
    period: 'forever free',
    description: 'Perfect for getting started',
    features: [
      'Accounting module',
      'E-commerce basics',
      'POS system',
      '5 invoices/month',
      'Email support',
    ],
    notIncluded: [
      'Website analytics',
      'Social media planner',
      'AI tools',
      'Priority support',
    ],
    popular: false,
    cta: 'Get Started',
  },
  {
    id: 'plus',
    name: 'Plus',
    price: 499,
    period: 'per month',
    description: 'Best for growing businesses',
    features: [
      'Everything in Essentials',
      'Website analytics',
      'Social media planner',
      'Unlimited invoices',
      'Chat support',
    ],
    notIncluded: [
      'AI social media generation',
      'Brand management',
      'AI app building',
    ],
    popular: true,
    cta: 'Start Free Trial',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 1299,
    period: 'per month',
    description: 'Advanced tools for professionals',
    features: [
      'Everything in Plus',
      'AI social media generation',
      'Brand management tools',
      'Custom reports',
      'Priority chat support',
    ],
    notIncluded: [
      'Complete AI automation',
      'AI app building service',
    ],
    popular: false,
    cta: 'Go Pro',
  },
  {
    id: 'ultra',
    name: 'Ultra',
    price: 3499,
    period: 'per month',
    description: 'Enterprise-grade everything',
    features: [
      'Everything in Pro',
      'Complete AI marketing automation',
      'AI app building service',
      'Dedicated account manager',
      '24/7 phone support',
      'Custom integrations',
    ],
    notIncluded: [],
    popular: false,
    cta: 'Contact Sales',
  },
];

export const Pricing: React.FC = () => {
  return (
    <div className="pricing-page">
      <div className="page-header centered">
        <h1 className="page-title">Simple, Transparent Pricing</h1>
        <p className="page-subtitle">
          Whatever your status, our offers evolve according to your needs.
        </p>
      </div>

      <div className="pricing-grid">
        {plans.map((plan, index) => (
          <TiltCard
            key={plan.id} 
            className={`pricing-card ${plan.popular ? 'popular' : ''}`}
            shimmer
            spotlight
          >
            {plan.popular && (
              <div className="popular-badge">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Most Popular
              </div>
            )}
            
            <div className="pricing-header">
              <h3 className="plan-name">{plan.name}</h3>
              <p className="plan-description">{plan.description}</p>
              <div className="plan-price">
                <span className="currency">R</span>
                <span className="amount">{plan.price}</span>
                <span className="period">{plan.period}</span>
              </div>
            </div>

            <div className="pricing-features">
              <h4>Features included:</h4>
              <ul className="feature-list">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="included">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    {feature}
                  </li>
                ))}
                {plan.notIncluded.map((feature, idx) => (
                  <li key={idx} className="not-included">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <RippleButton 
              variant={plan.popular ? 'primary' : 'default'}
              className="cta-button"
              style={{ width: '100%' }}
            >
              {plan.cta}
            </RippleButton>
          </TiltCard>
        ))}
      </div>

      <TiltCard className="pricing-faq" shimmer spotlight>
        <h3>Frequently Asked Questions</h3>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Can I change plans anytime?</h4>
            <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
          </div>
          <div className="faq-item">
            <h4>Is there a free trial?</h4>
            <p>Yes, Plus and Pro plans come with a 14-day free trial. No credit card required.</p>
          </div>
          <div className="faq-item">
            <h4>What payment methods do you accept?</h4>
            <p>We accept all major credit cards, PayPal, and bank transfers for Ultra plans.</p>
          </div>
          <div className="faq-item">
            <h4>Do you offer refunds?</h4>
            <p>Yes, we offer a 30-day money-back guarantee on all paid plans.</p>
          </div>
        </div>
      </TiltCard>
    </div>
  );
};
